import {
  Stack,
  Box,
  Fade,
  Paper,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useState } from "react";
import { BundlePatientSummary } from "@/services/fhir/patientsummary/BundlePatientSummary";

import { useFhirData } from "@/utils/useFhirData";

import { TypeSelector, ContentType } from "./components/TypeSelector";
import { PatientSelector } from "./components/PatientSelector";
import { Medications } from "./components/Medications/Medications";
import { Exams } from "./components/Exams/Exams";
import { HTTPResponseStatus } from "@/utils/enum";
import { toast } from "react-toastify";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { useFormatter } from "@/utils/useFormatter";

interface Patient {
  id: string;
  name: string;
  phone?: string;
  cpf?: string;
  birthDate?: string;
  gender?: string;
  resource?: any; // Recurso FHIR completo para uso futuro
}

/**
 * Componente principal de pós-atendimento com fluxo otimizado em etapas
 * Fluxo: 1) Selecionar tipo (Medicamentos/Exames) -> 2) Selecionar paciente -> 3) Visualizar dados
 * Melhora a experiência do usuário ao focar em uma tarefa por vez
 */
export function PostAttendence() {
  const { patients: listPatients } = usePatientOrganizationContext();

  // Estado para controlar o tipo de conteúdo selecionado
  const [contentType, setContentType] = useState<ContentType>(null);

  // Estado para controlar o paciente selecionado
  const [selectedPatient, setSelectedPatient] = useState<string>("");

  // Estado para armazenar os dados do summary do paciente
  const [summary, setSummary] = useState<any>(null);

  // Estado para controlar se já foi realizada uma busca
  const [hasSearched, setHasSearched] = useState(false);

  // Estado de carregamento
  const [loading, setLoading] = useState(false);

  // Processa e ordena a lista de pacientes do contexto
  // Extrai dados adicionais como CPF, data de nascimento, gênero, etc.
  const patients = listPatients
    ?.map((entry: any) => {
      const resource = entry.resource;

      // Encontra o número de telefone no array telecom
      const phone = resource.telecom?.find(
        (t: any) => t.system === "phone",
      )?.value;

      // Extrai CPF do array identifier
      const cpfIdentifier = resource.identifier?.find(
        (id: any) =>
          id.type?.coding?.[0]?.code === "TAX" ||
          id.system?.includes("cpf") ||
          id.system?.includes("CPF"),
      );
      const cpf = cpfIdentifier?.value;

      // Extrai data de nascimento
      const birthDate = resource.birthDate;

      // Extrai gênero
      const gender = resource.gender;

      // Extrai nome seguindo o mesmo padrão de PatientSearch (official/usual + fallbacks)
      const officialName = resource.name?.find(
        (n: any) => n.use === "usual" || n.use === "official",
      );
      const nameFromGivenFamily = officialName
        ? `${officialName.given?.join(" ") || ""} ${officialName.family || ""}`.trim()
        : "";
      const auxName =
        officialName?.text ||
        nameFromGivenFamily ||
        resource.name?.[0]?.text ||
        resource.subject?.display ||
        "";
      const name = useFormatter.formatNameDisplay(auxName) || "";

      return {
        id: resource.id,
        name,
        phone: phone ? phone.replace(/^55/, "") : undefined, // Remove código do país se presente
        cpf: cpf,
        birthDate: birthDate,
        gender: gender,
        resource: resource, // Mantém o recurso completo para uso futuro
      };
    })
    ?.filter((patient: Patient) => patient.name)
    ?.sort((a: Patient, b: Patient) =>
      (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
    );

  /**
   * Manipula a seleção do tipo de conteúdo
   * Reseta o estado quando um novo tipo é selecionado
   */
  const handleTypeSelect = (type: ContentType) => {
    setContentType(type);
    setSelectedPatient("");
    setSummary(null);
    setHasSearched(false);
  };

  /**
   * Manipula o retorno para seleção de tipo
   * Reseta todos os estados relacionados ao paciente
   */
  const handleBackToType = () => {
    setContentType(null);
    setSelectedPatient("");
    setSummary(null);
    setHasSearched(false);
  };

  /**
   * Manipula a mudança de paciente selecionado
   * Reseta os dados do summary quando um novo paciente é selecionado
   */
  const handlePatientChange = (patientId: string) => {
    setSelectedPatient(patientId);
    setSummary(null);
    setHasSearched(false);
  };

  /**
   * Manipula o retorno para seleção de pacientes
   * Mantém o tipo de conteúdo selecionado, apenas volta para seleção de paciente
   */
  const handleBackToPatientSelection = () => {
    setSelectedPatient("");
    setSummary(null);
    setHasSearched(false);
  };

  /**
   * Busca os dados do paciente selecionado
   * Carrega MedicationRequest, ServiceRequest e DocumentReference
   */
  const handleSearch = async () => {
    if (!selectedPatient) return;

    setLoading(true);
    try {
      const response = await BundlePatientSummary({
        identifier: selectedPatient.split("-")[1],
        entries: ["MedicationRequest", "ServiceRequest", "DocumentReference"],
      });

      if (response.status === HTTPResponseStatus.Forbidden) {
        toast.warn("Verifique as opções de consentimento");
        return;
      }

      setSummary(response);
      setHasSearched(true);
    } catch (error) {
      toast.error(
        "Não foi possível buscar os dados, tente novamente mais tarde.",
      );
      console.error("Erro ao buscar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  // Extrai os dados necessários do summary para medicamentos
  const medicationData = useFhirData.extractPatientSummaryResource(
    summary,
    "MedicationRequest",
  )?.[0]?.entry;

  // Extrai os dados necessários do summary para exames
  const serviceRequestData = useFhirData.extractPatientSummaryResource(
    summary,
    "ServiceRequest",
  );

  const documentReferenceServiceRequest =
    useFhirData.extractPatientSummaryResource(
      summary,
      "DocumentReference",
      "service-request",
    );

  const documentReferencePrescription =
    useFhirData.extractPatientSummaryResource(
      summary,
      "DocumentReference",
      "prescricao",
    );

  const resultsData = useFhirData.extractPatientSummaryResource(
    summary,
    "DocumentReference",
    "resultado",
  );

  // Dados do paciente selecionado para passar aos componentes filhos
  const patientData = patients?.find((p) => p.id === selectedPatient);

  return (
    <Stack
      sx={{
        backgroundColor: "neutral0.main",
        borderRadius: 2,
        width: "100%",
        padding: 3,
        mb: 2,
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
        minHeight: "400px",
      }}
    >
      {/* Etapa 1: Seleção do tipo de conteúdo */}
      {!contentType && (
        <Fade in={!contentType}>
          <Box>
            <TypeSelector onSelectType={handleTypeSelect} />
          </Box>
        </Fade>
      )}

      {/* Etapa 2: Seleção do paciente */}
      {contentType && !hasSearched && (
        <Fade in={contentType !== null && !hasSearched}>
          <Box>
            <PatientSelector
              patients={patients || []}
              loading={loading}
              selectedPatient={selectedPatient}
              contentType={contentType}
              onPatientChange={handlePatientChange}
              onSearch={handleSearch}
              onBack={handleBackToType}
            />
          </Box>
        </Fade>
      )}

      {/* Etapa 3: Visualização dos dados */}
      {hasSearched && selectedPatient && contentType && (
        <Fade in={hasSearched}>
          <Box sx={{ width: "100%" }}>
            {/* Header com breadcrumb e botão de voltar */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <IconButton
                    onClick={handleBackToPatientSelection}
                    disabled={loading}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={
                          contentType === "medications"
                            ? "Medicamentos"
                            : "Exames"
                        }
                        size="small"
                        color={
                          contentType === "medications"
                            ? "primary"
                            : "secondary"
                        }
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        →
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {patientData?.name || "Paciente"}
                      </Typography>
                      {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {loading
                        ? "Carregando dados..."
                        : `Visualizando ${contentType === "medications" ? "medicamentos" : "exames"} do paciente`}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Mostra apenas o componente correspondente ao tipo selecionado */}
            {loading && !summary ? (
              // Skeleton de carregamento enquanto busca os dados pela primeira vez
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: "background.paper",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="40%" height={32} />
                      <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                  </Stack>
                  <Skeleton
                    variant="rectangular"
                    height={200}
                    sx={{ borderRadius: 1 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Skeleton variant="rounded" width={100} height={36} />
                    <Skeleton variant="rounded" width={100} height={36} />
                    <Skeleton variant="rounded" width={100} height={36} />
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <>
                {contentType === "medications" && (
                  <Medications
                    data={medicationData}
                    documentReference={documentReferencePrescription}
                    onUpdate={handleSearch}
                    patientData={patientData}
                  />
                )}

                {contentType === "exams" && (
                  <Exams
                    data={documentReferenceServiceRequest}
                    resultsData={resultsData}
                    exames={serviceRequestData}
                    loading={loading}
                    onUpdate={handleSearch}
                    patientData={patientData}
                  />
                )}
              </>
            )}
          </Box>
        </Fade>
      )}
    </Stack>
  );
}
